import { useState, useEffect } from 'react';
import { FlaskConical, X } from 'lucide-react';

/** Top-right pill surfacing "you are in the test environment" to anyone
 * hitting a non-prod hostname (`platform.discoverygem.dev`, the dev
 * Netlify URL, or localhost). Mirrors the App-side pill so super-admins
 * working between Platform + App don't lose the signal as they cross
 * surfaces. Dismissible per session — clicking the X hides it until the
 * tab is reloaded. */
const DISMISS_KEY = 'dg-test-env-pill-dismissed';

function detectIsTestEnv(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return true;
  if (host === 'discoverygem.dev' || host.endsWith('.discoverygem.dev')) return true;
  if (host.endsWith('.netlify.app')) return true;
  return false;
}

export default function TestEnvPill() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!detectIsTestEnv()) return;
    if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-label="Test environment notice"
      className="fixed top-2 right-2 z-[60] flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white shadow-lg shadow-amber-500/30 ring-1 ring-amber-600/40"
    >
      <FlaskConical className="h-3.5 w-3.5" />
      <span>Test environment</span>
      <button
        type="button"
        onClick={() => {
          sessionStorage.setItem(DISMISS_KEY, '1');
          setVisible(false);
        }}
        className="ml-1 -mr-1 rounded-full p-0.5 hover:bg-amber-600/40 focus:outline-none focus:ring-1 focus:ring-white/70"
        aria-label="Dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
